<template>
  <div class="mb-5">
    <div id="program-health" class="box">
      <div class="columns">
        <div class="column">
          <ProgramStatItem
            stat-title="Total Members"
            stat-subtitle="Today"
            :program-id="program.id"
            :stat-type="ProgramStatType.MEMBER_COUNT"
          />
        </div>
        <div class="column">
          <ProgramStatItem
            stat-title="Steps & Activities"
            stat-subtitle="Today"
            :program-id="program.id"
            :stat-type="ProgramStatType.AGGREGATE_DATA_TODAY"
          />
        </div>
        <div class="column">
          <ProgramStatItem
            stat-title="Steps & Activities"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.AGGREGATE_DATA_ALL_TIME"
          />
        </div>
        <div class="column">
          <ProgramStatItem
            stat-title="Steps & Activities"
            stat-subtitle="Daily Average"
            :program-id="program.id"
            :stat-type="ProgramStatType.AGGREGATE_DATA_AVG_DAILY"
          />
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <ProgramStatItem
            :stat-title="programUnits(programSettingsCopy) + ' Walked'"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.MILES_WALKED_ALL_TIME"
          />
        </div>
        <div v-if="!isCoreTier && isTeamsFeatureOn" class="column">
          <ProgramStatItem
            v-if="isTeamsFeatureOn"
            stat-title="Total Teams"
            stat-subtitle="Today"
            :program-id="program.id"
            :stat-type="ProgramStatType.TOTAL_TEAMS"
          />
        </div>
        <div class="column">
          <ProgramStatItem
            stat-title="Photo/Journal Entries"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.PHOTO_JOURNAL_ENTRIES_ALL_TIME"
          />
        </div>
        <div class="column">
          <ProgramStatItem
            stat-title="Wellness Entries"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.WELLNESS_ENTRIES_ALL_TIME"
          />
        </div>
      </div>
      <div v-if="isCoreTier" class="columns">
        <div class="column">
          <ProgramStatItem
            stat-title="Challenges"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.CHALLENGES_TOTAL_ALL_TIME"
          />
        </div>
        <div class="column">
          <ProgramStatItem
            stat-title="Total Challenge Steps"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.CHALLENGES_TOTAL_STEPS_ALL_TIME"
          />
        </div>
        <div v-if="isCoreTier" class="column">
          <ProgramStatItem
            stat-title="Step Entries"
            stat-subtitle="All Time"
            :program-id="program.id"
            :stat-type="ProgramStatType.AGGREGATE_ENTRIES_ALL_TIME"
          />
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <WtTitle size="h5">Daily Steps & Activities</WtTitle>
          <p class="subtitle is-6">Last 7 days</p>

          <LineChart
            :chart-data="dailyStepsAndActivitiesGraphData()"
            :y-axes-ticks-min="0"
            :y-axes-ticks-max="dailyStepsAndActivitiesGraphMax"
            :y-axes-ticks-step-size="dailyStepsAndActivitiesGraphStepSize"
          />
        </div>
        <div class="column">
          <Top5Walkers
            v-if="program.id && !_.isEmpty(top5WalkersChartData)"
            title="Most Steps & Activities"
            sub-title="Last 7 days"
            leaderboard-tracking="steps&activities"
            :data="top5WalkersChartData"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LineChart from "@/charts/LineChart";
import WtTitle from "@@/WtTitle/WtTitle";
import Top5Walkers from "@@/WtLeaderboard/Top5Walkers";
import { mapActions, mapGetters } from "vuex";
import { unitSettingsMixin } from "@/mixins/unitSettingsMixin";
import ProgramStatItem from "@@/Program/ProgramStatItem.vue";
import { ProgramStatType } from "@/programStatType";

const red = "#F25200";

export default {
  name: "ProgramHealth",

  components: {
    ProgramStatItem,
    WtTitle,
    LineChart,
    Top5Walkers
  },

  mixins: [unitSettingsMixin],

  props: {
    program: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      dailyStepsAndActivitiesGraphLabels: [],
      dailyStepsAndActivitiesGraphTotalStepsPerDate: [],
      dailyStepsAndActivitiesGraphMax: 0,
      dailyStepsAndActivitiesGraphStepSize: 0,
      top5WalkersChartData: []
    };
  },

  computed: {
    ProgramStatType() {
      return ProgramStatType;
    },
    ...mapGetters("program", [
      "isCoreTier",
      "isTeamsFeatureOn",
      "programSettingsCopy"
    ])
  },

  async mounted() {
    await this.getData();
  },

  methods: {
    ...mapActions("program", [
      "fetchDailyStepsAndActivitiesGraphData",
      "fetchLeaderboardData"
    ]),

    async getData() {
      try {
        await this.getTop5WalkersChartData(this.program.id);

        // prepare graph data.
        this.fetchDailyStepsAndActivitiesGraphData(this.program.id)
          .then(data => {
            this.avgDailyStepsChartData(data);
          })
          .catch(e => {
            this.$buefy.toast.open("Failed to load program stats.");

            console.error(e);
          });
      } catch (e) {
        this.$buefy.toast.open("Failed to load program stats.");

        console.error(e);
      }
    },

    async getTop5WalkersChartData(programId) {
      let data = await this.fetchLeaderboardData(programId);
      this.top5WalkersChartData = data.data;
    },

    avgDailyStepsChartData(data) {
      let parsedData = JSON.parse(JSON.stringify(data));
      let chartData = [];

      // get the totals and dates
      parsedData.forEach(data => {
        let date = data.date;
        if (date in chartData) {
          chartData[date] = chartData[date] + data.steps + data.activities;
        } else {
          chartData[date] = data.steps + data.activities;
        }
      });

      // break the dates and data into two arrays for the chart
      for (const key in chartData) {
        this.dailyStepsAndActivitiesGraphLabels.push(key);
        this.dailyStepsAndActivitiesGraphTotalStepsPerDate.push(chartData[key]);
      }
    },

    dailyStepsAndActivitiesGraphData() {
      // format labels
      const formattedLabels = this.dailyStepsAndActivitiesGraphLabels.map(day =>
        this.moment(day).format("MMM DD")
      );

      // calculate max step and step size
      const biggestNumberFromChart = Math.max(
        ...this.dailyStepsAndActivitiesGraphTotalStepsPerDate
      );
      const maxStepSize =
        (parseInt(`${biggestNumberFromChart}`[0]) + 1) *
        Math.pow(10, `${biggestNumberFromChart}`.length - 1);

      this.dailyStepsAndActivitiesGraphMax = maxStepSize;
      this.dailyStepsAndActivitiesGraphStepSize = maxStepSize / 10;

      return {
        labels: formattedLabels,
        datasets: [
          {
            label: "",
            borderColor: red,
            backgroundColor: "transparent",
            pointBorderWidth: 3,
            pointRadius: 6,
            lineTension: 0,
            data: this.dailyStepsAndActivitiesGraphTotalStepsPerDate
          }
        ]
      };
    }
  }
};
</script>
